import { useMemo, useState } from "react";
import { CheckCircle2, Handshake, Search } from "lucide-react";
import { CardActions, EditableStars, Empty, Section } from "./redeShared";
import { displayName } from "./shared";

export const PrestadoresView = ({ leads, onOpen, onChanged }) => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const matchQuery = (p) => {
    if (!q) return true;
    const hay = [
      displayName(p),
      ...(p.payload?.specialties || []),
      ...(p.payload?.cities || []),
      p.payload?.state || "",
    ]
      .join(" ")
      .toLowerCase();
    return q.split(/\s+/).every((term) => hay.includes(term));
  };

  const partners = useMemo(
    () =>
      leads
        .filter((l) => l.type === "partner" && l.status === "parceria_fechada")
        .filter(matchQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads, q]
  );
  const blocked = useMemo(
    () =>
      leads
        .filter((l) => l.type === "partner" && l.status === "bloqueado")
        .filter(matchQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads, q]
  );

  return (
    <div data-testid="prestadores-view">
      <h1 className="font-display text-2xl font-bold text-petrol sm:text-3xl">
        Prestadores parceiros
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Profissionais com parceria fechada. Toque nas estrelas para avaliar.
      </p>

      <div className="relative mt-5 max-w-md">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome, serviço ou cidade"
          data-testid="prestadores-search-input"
          className="h-11 w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 text-sm outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20"
        />
      </div>

      <Section
        title="Rede ativa"
        sub={
          q
            ? `${partners.length} prestador(es) encontrados para "${query.trim()}".`
            : `${partners.length} prestador(es) na rede.`
        }
        testId="prestadores-ativos"
      >
        {partners.length === 0 ? (
          <Empty>
            {q
              ? "Nenhum prestador encontrado para essa busca."
              : "Nenhum prestador com parceria fechada ainda. Aceite os cadastros na lista de Leads."}
          </Empty>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {partners.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(p)}
                onKeyDown={(e) => e.key === "Enter" && onOpen(p)}
                data-testid={`rede-partner-card-${p.id}`}
                className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 text-left transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-600">
                    <Handshake className="h-5 w-5" />
                  </span>
                  <CardActions lead={p} onChanged={onChanged} />
                </div>
                <p className="mt-3 truncate font-display text-base font-bold text-petrol">
                  {displayName(p)}
                </p>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {(p.payload?.specialties || []).slice(0, 3).join(", ")}
                </p>
                <p className="mt-2 font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
                  {(p.payload?.cities || []).slice(0, 3).join(", ")} — {p.payload?.state}
                </p>
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <EditableStars lead={p} onChanged={onChanged} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {blocked.length > 0 && (
        <Section
          title="Bloqueados"
          sub="Prestadores bloqueados não entram em indicações."
          testId="prestadores-bloqueados"
        >
          <ul className="space-y-2">
            {blocked.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50/50 px-4 py-3"
                data-testid={`rede-blocked-${b.id}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-petrol">
                    {displayName(b)}
                  </span>
                  <span className="text-xs text-slate-500">
                    {(b.payload?.specialties || []).slice(0, 3).join(", ")}
                  </span>
                </span>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const { api } = await import("@/lib/api");
                    await api.patch(`/admin/leads/${b.id}`, { status: "novo" }).catch(() => {});
                    onChanged();
                  }}
                  data-testid={`rede-unblock-${b.id}`}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Desbloquear
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};
